import { convertFromRaw, RawDraftContentState } from 'draft-js'
import { stateToHTML } from 'draft-js-export-html'

import {
  IImageContent,
  IVideoContent,
  IAudioContent,
  IEditorContent,
  IExerciseContent,
  ILessonPageStateContent,
  IActionEditorContent,
  ILessonPageStateType,
} from '@models'
import axios, { AxiosResponse } from 'axios'
import arrayBufferToAudioBuffer from 'arraybuffer-to-audiobuffer'
import { logger } from '@utils/ConsoleLogger'
import { FIREBASE_CONFIG } from '@utils/constants'

export const transformContent = (rowContent: RawDraftContentState): string => {
  try {
    return stateToHTML(convertFromRaw(rowContent), {
      inlineStyles: {
        black: { style: { color: '#111111' } },
        darkGrey: { style: { color: '#404753' } },
        darkBlue: { style: { color: '#113a7b' } },
        blue: { style: { color: '#0053d7' } },
        lightBlue: { style: { color: '#2c82ff' } },
        grey: { style: { color: '#8ca4c0' } },
        orange: { style: { color: '#ff8c00' } },
        red: { style: { color: '#dc0000' } },
      },
    })?.replaceAll('<a href', `<a target="_blank" href`)
  } catch (e) {
    console.error('transformContent', e)
    return ''
  }
}

export const shuffle = (array: string[]): string[] => {
  let currentIndex = array.length
  let temporaryValue: string
  let randomIndex: number
  while (0 !== currentIndex) {
    randomIndex = Math.floor(Math.random() * currentIndex)
    currentIndex -= 1
    temporaryValue = array[currentIndex]
    array[currentIndex] = array[randomIndex]
    array[randomIndex] = temporaryValue
  }
  return array
}

export const sortElements = items =>
  [...items].sort((a, b) => ((a.droppedIndex || 0) > (b.droppedIndex || 0) ? 1 : -1))

export const isHiddenForStudent = (
  type: ILessonPageStateType,
  content: ILessonPageStateContent,
  isAllowedToDisplayByTeacher?: boolean
): boolean => {
  if (ILessonPageStateType['action-editor'] === type || ILessonPageStateType.editor === type) {
    if (content && 'color' in content && 'rowContent' in content) {
      const editorContent = content as IActionEditorContent
      if (
        ['lightBlue', 'blue'].includes(editorContent.color) &&
        hasOnlyItalicStyles(editorContent.rowContent)
      )
        return true
    }
  }

  if (typeof isAllowedToDisplayByTeacher === 'boolean') return !isAllowedToDisplayByTeacher
  return (content as IActionEditorContent)?.visibleForStudent === false
}

export const getIsEmptyBlock = (
  type: ILessonPageStateType,
  content: ILessonPageStateContent
): boolean => {
  let isEmpty: boolean
  if (!content) {
    return true
  }
  switch (type) {
    case ILessonPageStateType.video:
      isEmpty = !Boolean((content as IVideoContent)?.videoLink)
      break
    case ILessonPageStateType.audio:
      isEmpty = !Boolean((content as IAudioContent)?.audio)
      break
    case ILessonPageStateType.image:
      isEmpty = !Boolean((content as IImageContent)?.image)
      break
    case ILessonPageStateType.editor:
      isEmpty = !Boolean((content as IEditorContent)?.rowContent)
      break
    case ILessonPageStateType['action-editor']:
      isEmpty = !Boolean((content as IActionEditorContent)?.rowContent)
      break
    case ILessonPageStateType.exercise:
      isEmpty = !Boolean((content as IExerciseContent).variant)
      break
    default:
      isEmpty = false
  }

  return isEmpty
}
interface SoundInterface {
  type: string
  data: Uint8Array
}
export const playAudio = async (text: string) => {
  try {
    let res: AxiosResponse = await axios.get(`${FIREBASE_CONFIG.audioPronounceFnLink}?text=${text}`)
    let sound: SoundInterface = res.data.audioContent
    let audioCtx: AudioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    let arrayBuffer = new Uint8Array(sound.data).buffer
    let audioBuffer: AudioBuffer = await arrayBufferToAudioBuffer(arrayBuffer, audioCtx)
    let source: AudioBufferSourceNode = audioCtx.createBufferSource()
    source.buffer = audioBuffer
    source.connect(audioCtx.destination)
    source.start()
  } catch (error) {
    logger.error(error)
  }
}

export const findContextSentence = (text, targetIndex) => {
  const matches = [...text.matchAll(/\[(.*?)\]/g)]
  const customIndexes = matches.map((_, i) => 1 + i * 2)
  const matchIndex = customIndexes.indexOf(targetIndex)

  if (matchIndex === -1) return null

  const matchPosition = matches[matchIndex].index

  const punctuationMarks = /[.!?¡¿]/g

  let leftBoundary = Math.max(
    text.lastIndexOf('.', matchPosition),
    text.lastIndexOf('!', matchPosition),
    text.lastIndexOf('?', matchPosition),
    text.lastIndexOf('¡', matchPosition),
    text.lastIndexOf('¿', matchPosition)
  )

  if (leftBoundary !== -1 && text[leftBoundary + 1] === ' ') {
    leftBoundary += 1
  }

  if (leftBoundary !== -1 && /[¿¡]/.test(text[leftBoundary])) {
  } else {
    leftBoundary = leftBoundary === -1 ? 0 : leftBoundary + 1
  }

  const rightBoundaryMatch = text.slice(matchPosition).search(punctuationMarks)
  const rightBoundary =
    rightBoundaryMatch !== -1 ? matchPosition + rightBoundaryMatch + 1 : text.length

  let foundSentence = text.slice(leftBoundary, rightBoundary).trim()

  foundSentence = foundSentence.replace(/<[^>]*>/g, '')

  return foundSentence.replace(/\[(.*?)\]/g, '__')
}

export const hasOnlyItalicStyles = (rowContent: RawDraftContentState): boolean => {
  return rowContent?.blocks.every(
    block =>
      !block.inlineStyleRanges.length ||
      block.inlineStyleRanges.some(range => range.style === 'ITALIC')
  )
}
